import { useModal } from 'components/Modals';
import { ComponentPropsWithRef, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Async from 'react-select/async';

import { AddButton, PrimaryButton } from 'components/Buttons';
import FlashMessage from 'components/FlashMessage';

import { FLASH_MESSAGE_TYPES, flashMessageFactory, INITIAL_FLASH_MESSAGE } from 'constants/common';

import { ModalContent, ModalFooter, ModalHeader, Title } from 'styles/modals';
import { ButtonContainer, MaxWidthModal } from './styles';
import colors from 'styles/colors';
import { Vehicle } from 'services/resources';
import { useAppDispatch } from 'hooks/store';
import { getVehicles } from 'services/Vehicles/thunks';
import { VEHICLE_OWNERS } from 'constants/vehicles';
import { useSelector } from 'react-redux';
import { selectDataSourcesValues } from 'services/Telematics/selectors';
import { enrollVehicle, listDataSources } from 'services/Telematics/thunks';

const EnrollmentModal = ({
  setFlashMessage,
  closeModal,
}: {
  setFlashMessage: (message: typeof INITIAL_FLASH_MESSAGE) => void;
  closeModal: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const datasources = useSelector(selectDataSourcesValues());

  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const makeLabelFrom = (vehicle: Vehicle) => {
    const { vin, model } = vehicle;
    const { year, make, model: modelName } = model;

    const { stockNumber, licensePlate } = vehicle;

    const label = [vin];

    if (stockNumber) {
      label.push(stockNumber);
    }

    if (licensePlate) {
      label.push(licensePlate);
    }

    label.push([year, make, modelName].join(' '));

    return label.join(' | ');
  };

  const promiseOptions = (inputValue: string) =>
    dispatch(
      getVehicles(
        20,
        0,
        new URLSearchParams({
          owner: String(VEHICLE_OWNERS.fleet),
          model__make__in: datasources.map((ds) => ds.oemName).join(',') + ',',
        }),
        inputValue
      )
    ).then((values) => {
      if (!values || !Array.isArray(values.results)) {
        return [];
      }

      return values.results.map((vehicle) => ({
        value: vehicle.id,
        label: makeLabelFrom(vehicle),
      }));
    });

  const handleEnroll = async (e) => {
    try {
      await dispatch(enrollVehicle(selectedVehicle));
      setFlashMessage(
        flashMessageFactory({
          shown: true,
          type: FLASH_MESSAGE_TYPES.success,
          translationId: 'views.FleetSettings.OEMTelematics.Modals.Enroll.success',
        })
      );
      closeModal();
    } catch (error) {
      setFlashMessage(
        flashMessageFactory({
          shown: true,
          type: FLASH_MESSAGE_TYPES.error,
          translationId: 'views.FleetSettings.OEMTelematics.Modals.Enroll.error',
        })
      );
    }
  };

  return (
    <MaxWidthModal>
      <ModalHeader>
        <Title>
          <FormattedMessage id="views.FleetSettings.OEMTelematics.Modals.Enroll.title" />
        </Title>
      </ModalHeader>
      <form>
        <ModalContent>
          <p>
            <FormattedMessage id="views.FleetSettings.OEMTelematics.Modals.Enroll.description" />
          </p>
          <p>
            <FormattedMessage
              id="views.FleetSettings.OEMTelematics.Modals.Enroll.limitation"
              values={{ oems: datasources.map((datasource) => datasource.oemName).join(', ') }}
            />
          </p>
          <Async
            name="vehicle"
            loadOptions={promiseOptions}
            placeholder={formatMessage({ id: 'views.FleetSettings.OEMTelematics.Modals.Enroll.vinPlaceholder' })}
            onChange={(option) => setSelectedVehicle(option.value)}
            required
          />
        </ModalContent>
        <ModalFooter>
          <PrimaryButton onClick={handleEnroll}>
            <FormattedMessage id="views.FleetSettings.OEMTelematics.Modals.Enroll.enrollButton" />
          </PrimaryButton>
        </ModalFooter>
      </form>
    </MaxWidthModal>
  );
};

export const EnrollmentButton = (props: ComponentPropsWithRef<'button'>) => {
  const { formatMessage } = useIntl();
  const [flashMessageState, setFlashMessageState] = useState(INITIAL_FLASH_MESSAGE);

  const { openModal, isOpen, Modal, closeModal } = useModal({
    background: colors.modalBackground,
  });

  const onClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    openModal(event);
    if (props.onClick) {
      props.onClick(event);
    }
  };

  return (
    <ButtonContainer>
      <AddButton {...props} onClick={onClick}>
        <FormattedMessage id="views.FleetSettings.OEMTelematics.enrollNewVehicle" />
      </AddButton>
      {flashMessageState.shown && (
        <FlashMessage
          type={flashMessageState.type}
          text={formatMessage({ id: flashMessageState.translationId }, flashMessageState.translationValueObject)}
          onClose={() => {
            setFlashMessageState(INITIAL_FLASH_MESSAGE);
          }}
          visible
        />
      )}
      {isOpen && (
        <Modal>
          <EnrollmentModal setFlashMessage={setFlashMessageState} closeModal={closeModal} />
        </Modal>
      )}
    </ButtonContainer>
  );
};

export default { EnrollmentButton };
